import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import * as RRulePkg from 'rrule'
import { Toolbox } from '~/plugins/toolbox/shared'
dayjs.extend(utc)
dayjs.extend(timezone)
const { RRule } = RRulePkg

enum Frequency {
  YEARLY = 0,
  MONTHLY = 1,
  WEEKLY = 2,
  DAILY = 3,
  HOURLY = 4,
  MINUTELY = 5,
  SECONDLY = 6,
}

export type EndsScope = 'never' | 'on' | 'after'
export type RepeatScope = 'days' | 'weeks' | 'months'

type BuildOptions = {
  startsDate: dayjs.Dayjs
  startsHour: number
  repeatCount: number
  repeatDays: number[]
  repeatScope: RepeatScope
  endsScope: EndsScope
  endsDate: dayjs.Dayjs
  endsAfterCount: number
}

type Props = { timezone: string }

export const useRRule = ({
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: Props) => {
  const build = ({
    startsDate = dayjs().tz(timezone),
    startsHour = dayjs().tz(timezone).hour(),
    repeatDays,
    repeatScope,
    endsScope,
    endsDate,
    endsAfterCount,
    repeatCount,
  }: BuildOptions) => {
    const daysMap = [
      RRule.SU,
      RRule.MO,
      RRule.TU,
      RRule.WE,
      RRule.TH,
      RRule.FR,
      RRule.SA,
    ]

    const frequencyMap: Record<RepeatScope, Frequency> = {
      days: RRule.DAILY,
      weeks: RRule.WEEKLY,
      months: RRule.MONTHLY,
    }

    const days = repeatDays?.map(dayIndex => daysMap[dayIndex]) ?? []
    const frequency = frequencyMap[repeatScope]
    const isWeekly = frequency === RRule.WEEKLY

    let until = undefined
    let count = undefined

    if (endsScope === 'on' && endsDate) {
      until = endsDate.toDate()
    } else if (endsScope === 'after') {
      count = endsAfterCount
    }

    const dtstart = startsDate
      .hour(startsHour)
      .minute(0)
      .second(0)
      .millisecond(0)
      .utc()
      .toDate()

    const rule = new RRule({
      freq: frequency,
      interval: repeatCount,
      byweekday: isWeekly ? days : undefined,
      until,
      count,
      dtstart,
    })

    return rule.toString()
  }

  const parse = (rule: string) => {
    if (Toolbox.Utility.isNull(rule)) {
      return {}
    }

    const rrule = RRule.fromString(rule)

    const daysMap = [
      RRule.SU,
      RRule.MO,
      RRule.TU,
      RRule.WE,
      RRule.TH,
      RRule.FR,
      RRule.SA,
    ]

    const frequencyMap: Record<Frequency, RepeatScope> = {
      [Frequency.SECONDLY]: 'days',
      [Frequency.MINUTELY]: 'days',
      [Frequency.HOURLY]: 'days',
      [RRule.DAILY]: 'days',
      [RRule.WEEKLY]: 'weeks',
      [RRule.MONTHLY]: 'months',
      [Frequency.YEARLY]: 'months',
    }

    const repeatScope = frequencyMap[rrule.options.freq] ?? 'weeks'
    const repeatCount = rrule.options.interval ?? 1

    let startsDate: dayjs.Dayjs
    let startsHour: number
    let repeatDays: number[] = []
    let endsScope: EndsScope
    let endsDate: dayjs.Dayjs
    let endsAfterCount: number

    if (repeatScope === 'weeks') {
      repeatDays = rrule.options.byweekday?.map(day => day + 1) ?? []
    }

    if (rrule.options.count) {
      endsScope = 'after'
      endsAfterCount = rrule.options.count
    } else if (rrule.options.until) {
      endsScope = 'on'
      endsDate = dayjs.utc(rrule.options.until).tz(timezone)
    } else {
      endsScope = 'never'
    }

    if (rrule.options.dtstart) {
      const date = dayjs.utc(rrule.options.dtstart).tz(timezone)
      startsDate = date.startOf('day')
      startsHour = date.hour()
    }

    return {
      startsDate,
      startsHour,
      repeatScope,
      repeatCount,
      repeatDays,
      endsScope,
      endsDate,
      endsAfterCount,
    }
  }

  const getDateNext = (rrule: string) => {
    const rule = RRule.fromString(rrule)
    const dateNext = rule.after(dayjs().endOf('hour').toDate(), true)
    return dateNext
  }

  return {
    build,
    parse,
    getDateNext,
  }
}
